import { format, formatISO, parseISO } from "date-fns";
import { Employee } from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useEmployeeSearch";
import { VehicleSetVariant } from "features/CenterContent/RoleContent/VehicleChecklist/VehicleSetVariant";

export enum CheckListType {
  BRAKE_TEST = "BRAKE_TEST", // Bremseprøve
  IKS = "IKS", // Innsett (IKS)
  UKS = "UKS", // Uttak (UKS)
}

export enum RollingStockType {
  VEHICLE_SET = "VEHICLE_SET",
  VEHICLE = "VEHICLE",
}

export enum CompanyId {
  VYGRUPPEN = "110",
  VYTOG = "121",
  CARGONET = "310",
}

export const getCompanyName = (id: CompanyId) => {
  switch (id) {
    case CompanyId.VYGRUPPEN:
      return "Vygruppen (Øst)";
    case CompanyId.VYTOG:
      return "Vy Tog (Vest)";
    case CompanyId.CARGONET:
      return "CargoNet";
  }
};

export const getChecklistText = (type: CheckListType) => {
  switch (type) {
    case CheckListType.BRAKE_TEST:
      return "Bremseprøve";
    case CheckListType.IKS:
      return "Innsett (IKS)";
    case CheckListType.UKS:
      return "Uttak (UKS)";
  }
};

export function formatTime(dateTime: string) {
  const date = parseISO(dateTime);
  return format(date, "HH:mm");
}

export function formatDate(dateTime: string) {
  const date = parseISO(dateTime);
  return format(date, "dd.MM.yyyy");
}

export function formatDateTime(dateTime: string) {
  return `${formatDate(dateTime)} kl ${formatTime(dateTime)}`;
}

export const normalizeDateForComparison = (dateString: string) => {
  if (!dateString) return "";
  const date = parseISO(dateString.toString());
  return formatISO(date);
};

/**
 * Performs locale-aware string comparison for Norwegian strings
 * needed for ÆØÅ
 * with case-insensitive matching (base sensitivity)
 *
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns Negative number if a < b, positive if a > b, zero if equal
 */
export const norwegianLocaleCompare = (a: string, b: string): number =>
  a.localeCompare(b, "no", { sensitivity: "base" });

// Enhanced sorting function that prioritizes first name matches for employees
export const createSearchableSortedList = (
  vehicleSetVariants: VehicleSetVariant[],
  employees: Employee[],
  searchQuery = "",
) => {
  const searchable = [
    ...vehicleSetVariants,
    ...employees.map((employee) => ({
      name: employee.displayName,
    })),
  ];

  // If no search query, just sort alphabetically
  if (!searchQuery.trim()) {
    return searchable.sort((a, b) => {
      const aValue = "setId" in a ? a.setId : a.name;
      const bValue = "setId" in b ? b.setId : b.name;
      return norwegianLocaleCompare(aValue, bValue);
    });
  }

  const searchTerm = searchQuery.toLowerCase();

  return searchable.sort((a, b) => {
    const aValue = "setId" in a ? a.setId : a.name;
    const bValue = "setId" in b ? b.setId : b.name;

    // For vehicle variants, use standard alphabetical sorting
    if ("setId" in a && "setId" in b) {
      return norwegianLocaleCompare(aValue, bValue);
    }

    // For employees vs vehicles, employees come first if they match
    if ("setId" in a && !("setId" in b)) {
      const bName = bValue.toLowerCase();
      const bFirstName = bName.split(" ")[0];
      return bFirstName.startsWith(searchTerm) ? 1 : -1;
    }

    if (!("setId" in a) && "setId" in b) {
      const aName = aValue.toLowerCase();
      const aFirstName = aName.split(" ")[0];
      return aFirstName.startsWith(searchTerm) ? -1 : 1;
    }

    // Both are employees - apply first name priority sorting
    if (!("setId" in a) && !("setId" in b)) {
      const aName = aValue.toLowerCase();
      const bName = bValue.toLowerCase();

      const aFirstName = aName.split(" ")[0];
      const bFirstName = bName.split(" ")[0];

      const aFirstNameMatch = aFirstName.startsWith(searchTerm);
      const bFirstNameMatch = bFirstName.startsWith(searchTerm);

      // If one matches first name and other doesn't, prioritize first name match
      if (aFirstNameMatch && !bFirstNameMatch) return -1;
      if (!aFirstNameMatch && bFirstNameMatch) return 1;

      // If both match first name OR both don't match first name, sort alphabetically
      return norwegianLocaleCompare(aValue, bValue);
    }

    // Fallback to standard sorting
    return norwegianLocaleCompare(aValue, bValue);
  });
};

export const removeDash = (str: string) => str.replace(/-/g, "");

// Check if search query contains digit or hyphen (indicates vehicle search)
export const hasDashOrDigit = (query: string) => /[\d-]/.test(query);
